import React from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import InfoTooltip from "@core/components/InfoTooltip";
import DepartmentSelectOptions from "@features/department/components/DepartmentSelectOptions";

export interface ActivityFormSelectorState {
    department_id: number;
    report_template_id: number;
    report_tag_id: number;
}

export interface ActivityFormSelectorProps {
    entityScope?: 'church' | 'district' | 'nation';
    entityId: number;
    selector: ActivityFormSelectorState;
    setSelector: React.Dispatch<React.SetStateAction<ActivityFormSelectorState>>;
    departments: any[];
    departmentsLoading: boolean;
    templates: any[];
    templatesLoading: boolean;
    tags: any[];
    tagsLoading: boolean;
    /** Called on submit once the selection is complete (parent navigates with the chosen params). */
    onOpen: () => void;
}

/**
 * The department / template / reporting-period selector shown when the activity entry
 * form has no params yet. Presentational; selection state + the "open" navigation are
 * supplied by the parent.
 */
export default function ActivityFormSelector({
    entityScope: _entityScope,
    entityId,
    selector,
    setSelector,
    departments,
    departmentsLoading,
    templates,
    templatesLoading,
    tags,
    tagsLoading,
    onOpen,
}: ActivityFormSelectorProps) {
    const canGo =
        entityId > 0 &&
        selector.department_id > 0 &&
        selector.report_template_id > 0 &&
        selector.report_tag_id > 0;

    const anyLoading = departmentsLoading || templatesLoading; //|| tagsLoading;
    const departmentsEmpty = !departmentsLoading && departments.length === 0;
    const templatesEmpty =
        selector.department_id > 0 && !templatesLoading && templates.length === 0;
    const tagsEmpty = !tagsLoading && tags.length === 0;

    return (
        <div className="activity-entry-form__wrap">
            <div className="ntcog-form-section" data-testid="missing-params-form-card">
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!canGo || anyLoading) return;
                        onOpen();
                    }}
                    className="d-flex flex-column gap-3"
                >
                    {/* Department */}
                    <Form.Group className="mb-3">
                        <Form.Label
                            style={{
                                fontSize: "14px",
                                fontWeight: 400,
                            }}>Department<InfoTooltip text="The department that owns this report template. Choose one to see its templates." /></Form.Label>
                        <Form.Select
                            aria-label="Select department"
                            value={selector.department_id ? String(selector.department_id) : ""}
                            disabled={departmentsEmpty}
                            onChange={(e) => {
                                const raw = e.target.value;
                                const id = raw === "" ? 0 : Number(raw);
                                setSelector((s) => ({
                                    ...s,
                                    department_id: id,
                                    report_template_id: 0, // reset dependent
                                }));
                            }}
                        >
                            <option value="">
                                {departmentsLoading
                                    ? "Loading departments…"
                                    : departmentsEmpty
                                      ? "No departments available"
                                      : "Select a department…"}
                            </option>
                            <DepartmentSelectOptions departments={departments} stringValues />
                        </Form.Select>
                    </Form.Group>

                    {/* Report Template */}
                    <Form.Group className="mb-3">
                        <Form.Label
                            style={{
                                fontSize: "14px",
                                fontWeight: 400,
                            }}>Report template<InfoTooltip text="The report template defines which activities and KPIs you will fill in." /></Form.Label>
                        <Form.Select
                            aria-label="Select report template"
                            value={selector.report_template_id ? String(selector.report_template_id) : ""}
                            disabled={!selector.department_id || templatesEmpty}
                            onChange={(e) => {
                                const raw = e.target.value;
                                setSelector((s) => ({
                                    ...s,
                                    report_template_id: raw === "" ? 0 : Number(raw),
                                }));
                            }}
                        >
                            <option value="">
                                {!selector.department_id
                                    ? "Select a department first…"
                                    : templatesLoading
                                      ? "Loading templates…"
                                      : templatesEmpty
                                        ? "No templates available"
                                        : "Select a template…"}
                            </option>
                            {templates.map((t: any) => (
                                <option key={t.report_template_id} value={String(t.report_template_id)}>
                                    {t.report_template_name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {/* Report Tag */}
                    <Form.Group className="mb-3">
                        <Form.Label
                            style={{
                                fontSize: "14px",
                                fontWeight: 400,
                            }}>Reporting period<InfoTooltip text="The date range (e.g. January 2025) for which you are submitting this report." /></Form.Label>
                        <Form.Select
                            aria-label="Select reporting period"
                            value={selector.report_tag_id ? String(selector.report_tag_id) : ""}
                            disabled={tagsLoading || tagsEmpty}
                            onChange={(e) => {
                                const raw = e.target.value;
                                setSelector((s) => ({ ...s, report_tag_id: raw === "" ? 0 : Number(raw) }));
                            }}
                        >
                            <option value="">
                                {tagsLoading
                                    ? "Loading reporting periods…"
                                    : tagsEmpty
                                      ? "No reporting periods available"
                                      : "Select a reporting period…"}
                            </option>
                            {tags.map((tg: any) => (
                                <option key={tg.report_tag_id} value={String(tg.report_tag_id)}>
                                    {tg.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <div className="d-flex justify-content-end pt-2">
                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            disabled={!canGo || anyLoading}
                            aria-busy={anyLoading}
                        >
                            {anyLoading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                                    Loading…
                                </>
                            ) : (
                                "Open form"
                            )}
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
}
